import React from "react";
import { Users, Cpu, Sparkles } from "lucide-react";

/**
 * HumanInTheLoopCard — the "human + AI oversight" differentiator.
 *
 * Variants:
 *   - "card"   Full quote-style block. Use on marketing / hero areas.
 *   - "ribbon" Slim single-row banner. Use on dashboards / utility pages.
 */
export const HumanInTheLoopCard = ({ variant = "card", className = "" }) => {
  if (variant === "ribbon") {
    return (
      <div
        data-testid="human-in-loop-ribbon"
        className={`relative overflow-hidden rounded-xl px-4 py-3 ${className}`}
        style={{
          background: "linear-gradient(160deg, #0B1524 0%, #122236 55%, #14283F 100%)",
          border: "1px solid rgba(94,234,212,0.22)",
          boxShadow: "0 12px 28px -18px rgba(0,0,0,0.80), 0 0 0 1px rgba(94,234,212,0.05)",
        }}
      >
        {/* Left accent bar */}
        <div
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-1"
          style={{ background: "linear-gradient(180deg, #2DD4BF 0%, #38BDF8 100%)" }}
        />
        {/* Top hairline accent (matches KPI card style) */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent 0%, rgba(20,184,166,0.55) 50%, transparent 100%)" }}
        />
        {/* Subtle teal corner glow */}
        <div
          aria-hidden="true"
          className="absolute -top-10 -right-10 h-28 w-28 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(20,184,166,0.18), transparent 70%)" }}
        />

        <div className="relative flex items-center gap-3 pl-2">
          <div className="flex -space-x-1 flex-shrink-0">
            <div
              className="h-7 w-7 rounded-full flex items-center justify-center"
              style={{ background: "rgba(20,184,166,0.18)", border: "1px solid rgba(20,184,166,0.42)" }}
            >
              <Users className="h-3.5 w-3.5" style={{ color: "#5EEAD4" }} />
            </div>
            <div
              className="h-7 w-7 rounded-full flex items-center justify-center"
              style={{ background: "rgba(56,189,248,0.18)", border: "1px solid rgba(56,189,248,0.42)" }}
            >
              <Cpu className="h-3.5 w-3.5" style={{ color: "#7DD3FC" }} />
            </div>
          </div>
          <p className="text-[13px] leading-snug" style={{ color: "#E2E8F0" }}>
            <span className="font-semibold" style={{ color: "#FFFFFF" }}>Human-supervised AI.</span>{" "}
            Unlike generic AI tools, Revenue Cycle IQ combines automation with{" "}
            <span className="font-semibold" style={{ color: "#5EEAD4" }}>active human oversight</span>{" "}
            to continuously refine denial intelligence and workflow recommendations.
          </p>
        </div>
      </div>
    );
  }

  // Default: "card" — quote-style block for the login hero / marketing
  return (
    <div
      data-testid="human-in-loop-card"
      className={`relative overflow-hidden rounded-2xl border border-teal-100 bg-white p-5 shadow-[0_2px_30px_rgba(15,23,42,0.06)] ${className}`}
    >
      {/* Left accent bar */}
      <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-teal-500 via-teal-400 to-sky-500" />
      {/* Subtle blob */}
      <div aria-hidden="true" className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-teal-100/40 blur-2xl" />

      <div className="relative pl-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex -space-x-1.5">
            <div className="h-7 w-7 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center">
              <Users className="h-3.5 w-3.5 text-teal-700" />
            </div>
            <div className="h-7 w-7 rounded-full bg-sky-50 border border-sky-200 flex items-center justify-center">
              <Cpu className="h-3.5 w-3.5 text-sky-700" />
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-teal-700 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Human-Supervised AI
          </span>
        </div>
        <p className="text-[13.5px] text-slate-700 leading-relaxed">
          Unlike generic AI tools, <span className="font-semibold text-slate-900">Revenue Cycle IQ combines automation with active human oversight</span>{" "}
          to continuously refine denial intelligence and workflow recommendations.
        </p>
      </div>
    </div>
  );
};

export default HumanInTheLoopCard;
